/**
 * THE PORTFOLIO, AS AREA — buildings nested under the portfolio that owns them.
 *
 * ── WHEN THIS IS BETTER THAN THE RANKED LIST, AND WHEN IT IS NOT ──
 *
 * A treemap answers one question a bar list cannot: "how is this portfolio's
 * volume distributed INSIDE it" — one building carrying eighty percent of a
 * portfolio is a shape you see instantly and a rank order you have to add up.
 *
 * It is worse than a bar list at everything else. Comparing two similar areas is
 * harder than comparing two bar lengths, and reading a value off an area is
 * nearly impossible. So:
 *
 *   · It renders ONLY when there is more than one portfolio. With one, the
 *     hierarchy has one level and the ranked list is strictly better — and the
 *     panel is not rendered at all rather than rendered with a caveat.
 *   · Every tile carries its NAME AND ITS NUMBER as text where it fits. An area
 *     nobody can put a number to is decoration.
 *   · The ranked list stays on the screen. This is an addition, not a
 *     replacement.
 *
 * ── THE HIERARCHY COMES FROM meta, NOT FROM THE BREAKDOWN ──
 *
 * `/analytics/breakdown` returns ONE dimension per panel: either portfolio
 * totals or building totals, never the nesting. `meta.buildings[].portfolioId`
 * is the parent link, so the tree is assembled here from the building rows the
 * league panel already fetched. This panel therefore costs NO extra request.
 *
 * A building whose portfolioId is null is not dropped and not silently adopted:
 * it goes under an explicit "No portfolio" parent, because a building missing
 * from a roll-up is the failure mode that makes people distrust the whole screen.
 */
import React, { useMemo } from 'react';
import { Tooltip, Treemap } from 'recharts';
import { AnalyticsBatchResult } from '../../../../../api/analytics-client';
import { palette } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable } from '../charts/chart-table-view';
import { TipCard, TipLabel, TipRow, TipStrong } from '../charts/chart.styled';
import { format } from '../charts/chart-theme';
import { slot as toneSlot } from '../charts/chart-palette';
import { FilterMeta } from '../state/filter-model';
import { readSlot, slotState } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';

const PANEL_ID = 'treemap';

interface Leaf {
  name: string;
  size: number;
  key: string;
  parent: string;
  fill: string;
}

interface Branch {
  name: string;
  key: string;
  fill: string;
  children: Leaf[];
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Recharts hands the content renderer raw geometry. Everything drawn here is
 *  drawn by hand, which is also what lets the tile carry its own text. */
const Tile: React.FC<any> = props => {
  const { x, y, width, height, name, size, fill, depth } = props;
  if (depth > 1 && (width < 4 || height < 4)) return null;

  const roomForText = width > 64 && height > 30;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={depth === 1 ? 'none' : fill}
        fillOpacity={depth === 1 ? 0 : 0.85}
        stroke={palette.surface}
        strokeWidth={depth === 1 ? 3 : 1}
      />
      {depth > 1 && roomForText ? (
        <>
          <text
            x={x + 8}
            y={y + 18}
            fill={palette.surface}
            fontSize={11}
            fontWeight={600}
          >
            {String(name).slice(0, Math.max(3, Math.floor(width / 7)))}
          </text>
          <text x={x + 8} y={y + 33} fill={palette.surface} fontSize={11}>
            {Number(size).toLocaleString()}
          </text>
        </>
      ) : null}
    </g>
  );
};

const TreemapTip: React.FC<any> = ({ active, payload, total, noun }) => {
  if (!active || !payload?.length) return null;
  const node = payload[0].payload;
  if (!node || node.depth === 1) return null;

  return (
    <TipCard role="tooltip">
      <TipLabel>{node.name}</TipLabel>
      <TipRow>
        <TipStrong>{Number(node.size || 0).toLocaleString()}</TipStrong>
        <span>{Number(node.size) === 1 ? noun[0] : noun[1]}</span>
        {total > 0 ? <span>· {format.share(Number(node.size || 0), total)}</span> : null}
      </TipRow>
      {node.parent ? (
        <TipRow>
          <span>in {node.parent}</span>
        </TipRow>
      ) : null}
    </TipCard>
  );
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface TreemapPanelProps {
  state: ChartShellState;
  /** The per-building breakdown the league panel already has. */
  league?: AnalyticsBatchResult;
  meta?: FilterMeta;
  drill: DrillHandlers;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const TreemapPanel: React.FC<TreemapPanelProps> = ({
  state,
  league,
  meta,
  drill,
  onExportPng,
  onExportCsv,
}) => {
  const slot = readSlot(league);
  const data = slot.ok;
  const noun = data?.noun || ['record', 'records'];

  const tree = useMemo<Branch[]>(() => {
    if (!data) return [];
    const portfolioOf = new Map<string, number | null>();
    for (const building of meta?.buildings || []) {
      portfolioOf.set(String(building.id), building.portfolioId ?? null);
    }
    const nameOf = new Map<string, string>();
    for (const portfolio of meta?.portfolios || []) {
      nameOf.set(String(portfolio.id), portfolio.name);
    }

    const groups = new Map<string, Branch>();
    let index = 0;

    for (const item of data.items) {
      const size = format.numberOf(item.value) ?? 0;
      // A zero-area tile cannot be drawn, and a treemap is not the place a
      // reader looks for "which building went quiet" — the ranked list is, and
      // it keeps the row.
      if (size <= 0) continue;

      const parentId = portfolioOf.get(item.key);
      // See the header: never silently adopted into somebody else's roll-up.
      const parentKey = parentId == null ? 'none' : String(parentId);
      const parentName =
        parentId == null ? 'No portfolio' : nameOf.get(parentKey) || `Portfolio ${parentKey}`;

      let branch = groups.get(parentKey);
      if (!branch) {
        branch = {
          name: parentName,
          key: parentKey,
          fill: toneSlot(index++),
          children: [],
        };
        groups.set(parentKey, branch);
      }
      branch.children.push({
        name: item.label,
        size,
        key: item.key,
        parent: parentName,
        fill: branch.fill,
      });
    }

    return [...groups.values()];
  }, [data, meta]);

  const total = tree.reduce(
    (sum, branch) => sum + branch.children.reduce((n, leaf) => n + leaf.size, 0),
    0
  );

  return (
    <ChartShell
      id={PANEL_ID}
      title="How the volume sits inside each portfolio"
      wide
      aspect={2.4}
      minHeight={300}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat="Area is volume; buildings are nested under the portfolio that owns them. This answers how CONCENTRATED a portfolio is — one building carrying most of it is a shape you see at once. For reading a value or ranking two buildings, the list above is better and it is still there."
      emptyReason={
        slot.empty || !tree.length
          ? {
              title: 'Nothing to lay out',
              text: 'No building recorded anything in this period, so there are no areas to draw.',
            }
          : null
      }
      summary={
        tree.length
          ? `Volume by portfolio and building: ${tree
              .map(
                branch =>
                  `${branch.name} ${branch.children
                    .reduce((n, leaf) => n + leaf.size, 0)
                    .toLocaleString()}`
              )
              .join(', ')}.`
          : undefined
      }
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      table={
        data ? (
          <ChartTable
            dimensionLabel="Building"
            valueLabel={data.label}
            showCompare={false}
            rows={tree.flatMap(branch =>
              branch.children.map(leaf => ({
                key: leaf.key,
                label: `${branch.name} — ${leaf.name}`,
                value: leaf.size.toLocaleString(),
              }))
            )}
          />
        ) : undefined
      }
    >
      <Treemap
        data={tree}
        dataKey="size"
        nameKey="name"
        aspectRatio={4 / 3}
        stroke={palette.surface}
        content={<Tile />}
        isAnimationActive={false}
        onClick={drill.onMark}
      >
        <Tooltip content={<TreemapTip total={total} noun={noun} />} />
      </Treemap>
    </ChartShell>
  );
};
